import React from "react";
import { css } from "@emotion/react";
import classNames from "classnames";
import {
    autoUpdate,
    FloatingOverlay,
    FloatingPortal,
    useDismiss,
    useFloating,
    useInteractions,
    useRole,
    FloatingFocusManager,
} from '@floating-ui/react';

import theme from "../../../theme";
import DefaultContainer from "./DefaultContainer";

type Props = {
    children: any,
    onClose?: (event: Event | undefined) => void,
    defaultStyling?: boolean,
    className?: string,
}

const _Modal = (
    {
        children,
        onClose,
        defaultStyling = true,
        className = '',
    }: Props
) => {
    const { refs, context } = useFloating({
        open: true,
        onOpenChange(open, event) {
            if (!open && onClose) onClose(event);
        },
        whileElementsMounted: autoUpdate,
    });

    const setModalElement = (ref: any) => {
        refs.setFloating(ref);
    };

    // There is an issue that clicking outside doesn't work after the internal click. Current react version 16.x
    // and floating-ui do not fit together about this issue. Having `capture: false` or updating react to version 17.x
    // fix this issue.
    const dismiss = useDismiss(context, { capture: false });
    const role = useRole(context);
    const { getFloatingProps } = useInteractions([dismiss, role]);

    const childrenProps = { setModalElement, modalAttributes: getFloatingProps() };

    return (
        <FloatingPortal>
            <FloatingOverlay
                {...defaultStyling && { css: defaultStyle }}
                className={classNames('modal-overlay', className)}
                lockScroll
            >
                <FloatingFocusManager context={context} guards={false} initialFocus={-1}>
                    {React.isValidElement(children) ? (
                        React.cloneElement(children, childrenProps)
                    ) : (
                        children(childrenProps)
                    )}
                </FloatingFocusManager>
            </FloatingOverlay>
        </FloatingPortal>
    );
};

const Modal = Object.assign(_Modal, {
    DefaultContainer,
});

export default Modal;

const defaultStyle = css`
    ${theme.mixins.modal.defaultOverlayStyle};
`;
